import type { NaturezaDoCusto, OrigemDoLancamento, TipoFinanceiro } from "./lancamento";
import type { CustoDoMaterial, LinhaDoDre, RegimeTributario } from "./linha-do-dre";
import { CATEGORIA_DAS_COMPRAS } from "./categorias-padrao";
import { CATEGORIAS_DA_FOLHA, provisaoDoMes } from "./folha";
import type { BaseDoMes } from "./resumo";

export type CategoriaDoDre = { id: string; nome: string; cor: string | null; natureza: NaturezaDoCusto; linhaDoDre: LinhaDoDre | null };
/** Lançamento da competência do mês, já sem os pares de estorno. */
export type LancamentoDoDre = { tipo: TipoFinanceiro; origem: OrigemDoLancamento | string; valorCents: number; categoria: CategoriaDoDre };
export type ConfigDoDre = {
  dreLigado: boolean;
  aliquotaSobreFaturamentoBps: number | null;
  custoDoMaterial: CustoDoMaterial;
  regime?: RegimeTributario | null;
  provisionarFolha?: boolean;
  taxaDeProvisaoBps?: number;
  aliquotaSobreLucroBps?: number | null;
};

/** IR e CSLL estimados todo mês (Lucro presumido com alíquota); o DARF lançado vira ajuste do trimestre. */
export const irEstimado = (c: ConfigDoDre) => c.dreLigado && c.regime === "LUCRO_PRESUMIDO" && !!c.aliquotaSobreLucroBps;
const provisaoLigada = (c: ConfigDoDre) => c.dreLigado && !!c.provisionarFolha;

/** Mês que fecha o trimestre (março, junho, setembro, dezembro). */
export const fechaOTrimestre = (competencia: string) => Number(competencia.slice(5, 7)) % 3 === 0;

export type BaseDoDre = {
  /** Serviços concluídos, retrabalhos cobrados e acréscimos do mês (competência). */
  faturamentoCents: number;
  /** Material consumido pela ficha técnica dos serviços entregues (1ª entrega) no mês. */
  consumoCents: number;
  /** Material das refações entregues no mês (o custo do retrabalho). */
  retrabalhoCents?: number;
  lancamentos: LancamentoDoDre[];
  config: ConfigDoDre;
  /** Salários (produção + administrativo) da folha do mês: a base da provisão de 13º e férias. */
  salariosDaFolhaCents?: number;
  /** No mês que fecha o trimestre: DARF de IR e CSLL lançados menos o estimado dos três meses. */
  ajusteDoTrimestreCents?: number;
  /** Depreciação do mês, por bem (já calculada). Só entra com o DRE ligado. */
  depreciacao?: ValorDaCategoria[];
};

export type ValorDaCategoria = { id: string; nome: string; cor: string | null; valorCents: number };
export type LinhaCalculada = { valorCents: number; categorias: ValorDaCategoria[] };

export const DESPESAS_OPERACIONAIS = ["PESSOAL", "OCUPACAO", "ADMINISTRATIVA", "COMERCIAL", "PERDAS_COM_CLIENTES", "OUTRAS"] as const;
export type DespesaOperacional = (typeof DESPESAS_OPERACIONAIS)[number];

export type Dre = {
  faturamentoCents: number;
  /** Receitas operacionais lançadas à mão (fora o faturamento). */
  outrasReceitas: LinhaCalculada;
  receitaBrutaCents: number;
  /** Descontos e abatimentos dados aos clientes. */
  descontos: LinhaCalculada;
  deducoes: LinhaCalculada & { estimado: boolean };
  receitaLiquidaCents: number;
  custoDosServicos: LinhaCalculada;
  lucroBrutoCents: number;
  despesas: Record<DespesaOperacional, LinhaCalculada>;
  despesasOperacionaisCents: number;
  /** Antes da depreciação (o EBITDA). */
  resultadoOperacionalCents: number;
  depreciacao: LinhaCalculada;
  financeiro: { entradas: LinhaCalculada; saidas: LinhaCalculada; valorCents: number };
  impostoSobreOLucro: LinhaCalculada & { estimado: boolean; estimadoCents: number };
  /** Provisão de 13º e férias do mês (já dentro de Pessoal). */
  provisaoCents: number;
  resultadoCents: number;
  /** Mexeu no caixa, não no resultado. */
  foraDoResultado: { entradas: LinhaCalculada; saidas: LinhaCalculada };
  /** Para a margem de contribuição da Visão do mês: o que é variável e o que é fixo. */
  margem: { custoDaProducaoCents: number; outrosVariaveisCents: number; fixosCents: number };
};

export const ID_DO_MATERIAL_CONSUMIDO = "material-consumido";
export const ID_DO_RETRABALHO = "custo-do-retrabalho";
export const ID_DO_IMPOSTO_ESTIMADO = "imposto-estimado";
export const ID_DA_PROVISAO = "provisao-da-folha";
export const ID_DO_IR_ESTIMADO = "ir-estimado";
export const ID_DO_AJUSTE_DO_TRIMESTRE = "ajuste-do-trimestre";
const pct = (bps: number) => (bps / 100).toLocaleString("pt-BR", { maximumFractionDigits: 2 });

const vazia = (): LinhaCalculada => ({ valorCents: 0, categorias: [] });

function somar(linha: LinhaCalculada, c: { id: string; nome: string; cor: string | null }, valorCents: number) {
  if (!valorCents) return;
  linha.valorCents += valorCents;
  const atual = linha.categorias.find((x) => x.id === c.id);
  if (atual) atual.valorCents += valorCents;
  else linha.categorias.push({ id: c.id, nome: c.nome, cor: c.cor, valorCents });
}

const ordenar = (l: LinhaCalculada) => l.categorias.sort((a, b) => b.valorCents - a.valorCents);

/** O bastante de um lançamento (ou movimento de caixa) para saber em que parte da conta ele cai. */
type MovimentoClassificavel = { origem: string; categoria: { nome: string; linhaDoDre: LinhaDoDre | null } };

/** O material lançado entra no resultado? Depende de como o custo do material é medido. */
function materialEntra(l: MovimentoClassificavel, config: ConfigDoDre) {
  if (l.categoria.nome !== CATEGORIA_DAS_COMPRAS) return true;
  if (!config.dreLigado) return l.origem !== "COMPRA";
  return config.custoDoMaterial === "COMPRAS";
}

/**
 * Saída que mexe no caixa mas não entra no resultado pela regra do mês: compra de material com o
 * custo pela ficha, 13º e férias com a provisão, DARF de IR com o IR estimado.
 */
export function saidaSoDeCaixa(l: MovimentoClassificavel, config: ConfigDoDre) {
  if (!materialEntra(l, config)) return true;
  if (provisaoLigada(config) && l.categoria.nome === CATEGORIAS_DA_FOLHA.decimoEFerias.nome) return true;
  return irEstimado(config) && l.categoria.linhaDoDre === "IMPOSTO_SOBRE_O_LUCRO";
}

/**
 * DRE gerencial do mês. Desligado (financeiro simples): sem imposto estimado e o material como
 * sempre foi (consumo da ficha + saídas de material fora as compras de estoque). Fora do resultado
 * e perdas valem nos dois.
 */
export function dreDoMes(base: BaseDoDre): Dre {
  const { config } = base;
  const consumoEntra = !config.dreLigado || config.custoDoMaterial === "FICHA";
  const outrasReceitas = vazia();
  const descontos = vazia();
  const deducoes = vazia();
  const custoDosServicos = vazia();
  const despesas = Object.fromEntries(DESPESAS_OPERACIONAIS.map((d) => [d, vazia()])) as Record<DespesaOperacional, LinhaCalculada>;
  const financeiro = { entradas: vazia(), saidas: vazia() };
  const impostoSobreOLucro = vazia();
  const fora = { entradas: vazia(), saidas: vazia() };
  const margem = { custoDaProducaoCents: 0, outrosVariaveisCents: 0, fixosCents: 0 };

  if (consumoEntra && base.consumoCents) {
    somar(custoDosServicos, { id: ID_DO_MATERIAL_CONSUMIDO, nome: "Material consumido na produção", cor: "#f97316" }, base.consumoCents);
    margem.custoDaProducaoCents += base.consumoCents;
  }
  if (consumoEntra && base.retrabalhoCents) {
    somar(custoDosServicos, { id: ID_DO_RETRABALHO, nome: "Custo do retrabalho", cor: "#dc2626" }, base.retrabalhoCents);
    margem.custoDaProducaoCents += base.retrabalhoCents;
  }

  for (const l of base.lancamentos) {
    // Recebimento é o caixa do faturamento, que já entrou pela produção.
    if (l.origem === "RECEBIMENTO") continue;
    const linha = l.categoria.linhaDoDre;
    if (l.tipo === "ENTRADA") {
      if (linha === "FORA_DO_RESULTADO") somar(fora.entradas, l.categoria, l.valorCents);
      else if (linha === "RESULTADO_FINANCEIRO") somar(financeiro.entradas, l.categoria, l.valorCents);
      else somar(outrasReceitas, l.categoria, l.valorCents);
      continue;
    }
    if (linha === "FORA_DO_RESULTADO") {
      somar(fora.saidas, l.categoria, l.valorCents);
      continue;
    }
    // Material com o custo pela ficha, 13º com a provisão, DARF com o IR estimado: só caixa.
    if (saidaSoDeCaixa(l, config)) continue;
    if (linha === "DESCONTO") somar(descontos, l.categoria, l.valorCents);
    else if (linha === "DEDUCAO") somar(deducoes, l.categoria, l.valorCents);
    else if (linha === "CUSTO_DO_SERVICO") somar(custoDosServicos, l.categoria, l.valorCents);
    else if (linha === "RESULTADO_FINANCEIRO") somar(financeiro.saidas, l.categoria, l.valorCents);
    else if (linha === "IMPOSTO_SOBRE_O_LUCRO") somar(impostoSobreOLucro, l.categoria, l.valorCents);
    else if (linha && linha in despesas) somar(despesas[linha as DespesaOperacional], l.categoria, l.valorCents);
    else somar(despesas.OUTRAS, l.categoria, l.valorCents);

    if (linha === "DEDUCAO" || linha === "DESCONTO" || l.categoria.natureza === "VARIAVEL") {
      if (linha === "CUSTO_DO_SERVICO") margem.custoDaProducaoCents += l.valorCents;
      else margem.outrosVariaveisCents += l.valorCents;
    } else margem.fixosCents += l.valorCents;
  }

  let provisaoCents = 0;
  if (provisaoLigada(config) && base.salariosDaFolhaCents) {
    provisaoCents = provisaoDoMes(base.salariosDaFolhaCents, config.taxaDeProvisaoBps ?? 0);
    somar(despesas.PESSOAL, { id: ID_DA_PROVISAO, nome: "Provisão de 13º e férias", cor: "#7c3aed" }, provisaoCents);
    margem.fixosCents += provisaoCents;
  }

  const receitaBrutaCents = base.faturamentoCents + outrasReceitas.valorCents;
  // Imposto lançado no mês vale; sem lançamento, a estimativa pela alíquota (só no DRE ligado).
  let estimado = false;
  // O imposto incide sobre o faturado menos os descontos dados.
  const baseDoImpostoCents = receitaBrutaCents - descontos.valorCents;
  if (config.dreLigado && !deducoes.valorCents && config.aliquotaSobreFaturamentoBps && baseDoImpostoCents > 0) {
    const valor = Math.round((baseDoImpostoCents * config.aliquotaSobreFaturamentoBps) / 10_000);
    somar(deducoes, { id: ID_DO_IMPOSTO_ESTIMADO, nome: `Estimado: ${pct(config.aliquotaSobreFaturamentoBps)}% do faturamento`, cor: "#ef4444" }, valor);
    margem.outrosVariaveisCents += valor;
    estimado = true;
  }
  let irEstimadoCents = 0;
  if (irEstimado(config)) {
    irEstimadoCents = Math.round((Math.max(0, receitaBrutaCents) * config.aliquotaSobreLucroBps!) / 10_000);
    somar(impostoSobreOLucro, { id: ID_DO_IR_ESTIMADO, nome: `Estimado: ${pct(config.aliquotaSobreLucroBps!)}% do faturamento`, cor: "#9f1239" }, irEstimadoCents);
    margem.outrosVariaveisCents += irEstimadoCents;
    const ajuste = base.ajusteDoTrimestreCents ?? 0;
    somar(impostoSobreOLucro, { id: ID_DO_AJUSTE_DO_TRIMESTRE, nome: "Ajuste do trimestre (DARF lançado − estimado)", cor: "#be123c" }, ajuste);
    margem.outrosVariaveisCents += ajuste;
  }

  for (const l of [outrasReceitas, descontos, deducoes, custoDosServicos, ...Object.values(despesas), financeiro.entradas, financeiro.saidas, impostoSobreOLucro, fora.entradas, fora.saidas]) ordenar(l);
  const depreciacao = vazia();
  if (config.dreLigado) for (const b of base.depreciacao ?? []) somar(depreciacao, b, b.valorCents);
  margem.fixosCents += depreciacao.valorCents;

  ordenar(depreciacao);
  const receitaLiquidaCents = receitaBrutaCents - descontos.valorCents - deducoes.valorCents;
  const lucroBrutoCents = receitaLiquidaCents - custoDosServicos.valorCents;
  const despesasOperacionaisCents = Object.values(despesas).reduce((t, d) => t + d.valorCents, 0);
  const resultadoOperacionalCents = lucroBrutoCents - despesasOperacionaisCents;
  const financeiroCents = financeiro.entradas.valorCents - financeiro.saidas.valorCents;
  return {
    faturamentoCents: base.faturamentoCents,
    outrasReceitas,
    receitaBrutaCents,
    descontos,
    deducoes: { ...deducoes, estimado },
    receitaLiquidaCents,
    custoDosServicos,
    lucroBrutoCents,
    despesas,
    despesasOperacionaisCents,
    resultadoOperacionalCents,
    depreciacao,
    financeiro: { ...financeiro, valorCents: financeiroCents },
    impostoSobreOLucro: { ...impostoSobreOLucro, estimado: irEstimadoCents > 0, estimadoCents: irEstimadoCents },
    provisaoCents,
    resultadoCents: resultadoOperacionalCents - depreciacao.valorCents + financeiroCents - impostoSobreOLucro.valorCents,
    foraDoResultado: fora,
    margem,
  };
}

/** A mesma conta na forma da Visão do mês (margem de contribuição): o resultado bate com o do DRE. */
export function baseDoMesDoDre(d: Dre): BaseDoMes {
  return {
    faturamentoCents: d.faturamentoCents,
    outrasReceitasCents: d.outrasReceitas.valorCents + d.financeiro.entradas.valorCents,
    custoProducaoCents: d.margem.custoDaProducaoCents,
    despesasVariaveisCents: d.margem.outrosVariaveisCents,
    custosFixosCents: d.margem.fixosCents,
  };
}
